import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    Alert,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    DialogActions,
    IconButton,
    Skeleton,
    Switch,
    Tooltip,
    Typography
} from "@mui/material";
import AddTwoToneIcon from "@mui/icons-material/AddTwoTone";
import ArrowBackTwoToneIcon from "@mui/icons-material/ArrowBackTwoTone";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import ArrowForwardTwoToneIcon from "@mui/icons-material/ArrowForwardTwoTone";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ContentCopyTwoToneIcon from "@mui/icons-material/ContentCopyTwoTone";
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";
import DynamicFormTwoToneIcon from "@mui/icons-material/DynamicFormTwoTone";
import ExpandLessTwoToneIcon from "@mui/icons-material/ExpandLessTwoTone";
import ExpandMoreTwoToneIcon from "@mui/icons-material/ExpandMoreTwoTone";
import SaveTwoToneIcon from "@mui/icons-material/SaveTwoTone";
import SearchTwoToneIcon from "@mui/icons-material/SearchTwoTone";
import VisibilityTwoToneIcon from "@mui/icons-material/VisibilityTwoTone";
import moment from "moment";
import {useHistory, useLocation, useParams} from "react-router-dom";
import AppPage from "../../widgets/layout/AppPage";
import SimpleDialog from "../../widgets/dialogs/SimpleDialog";
import AutocompleteSelect from "../../widgets/inputs/AutocompleteSelect";
import SolidTextField from "../../widgets/inputs/SolidTextField";
import DynamicFormExt from "../ticket/details/DynamicFormExt";
import {getDynamicFormConfig, updateOrCreateDynamicForm} from "../../../services/DynamicFormService";
import {generateFormId} from "../../../utils/SecureData";
import {CompanyStyle} from "../../../styles/pages/company/CompanyStyle";
import {GestioneModuliStyle} from "../../../styles/pages/modules/GestioneModuliStyle";

const FIELD_TYPES = {
    "Campi di Testo": {
        text: "Campo di testo semplice",
        "text-simple": "Testo semplice",
        "text-simple-centered": "Testo semplice centrato",
        "text-h1": "Titolo di sezione",
        "yellow-note": "Nota evidenziata in Giallo",
    },
    "Campi con Validazione": {
        date: "Data (dd/mm/yyyy)",
        email: "Indirizzo email",
        cellular: "Numero di cellulare",
        money: "Importo monetario",
        fiscal_code: "Codice Fiscale IT",
        vat_number: "Partita IVA IT",
    },
    "Campi di Selezione": {
        select: "Selezione singola da elenco",
        multiselect: "Selezione multipla da elenco",
        checkbox: "Opzione selezionabile (si/no)",
        "checkbox-text": "Opzione selezionabile (checkbox)",
        "checkbox-value": "Opzione selezionabile (valore/valore)",
    },
    "Campi complessi": {
        table: "Campo tabellare",
    },
};

const requiredOptions = [
    {value: "true", label: "Richiesto"},
    {value: "false", label: "Non richiesto"},
];

const fieldTypeOptions = Object.entries(FIELD_TYPES).flatMap(([group, options]) => (
    Object.entries(options).map(([value, label]) => ({
        value,
        label,
        group,
        searchText: `${group} ${label} ${value}`,
    }))
));

const fieldsWithOptions = ["select", "multiselect", "checkbox-value", "table"];
const ROW_SCROLL_OFFSET = 112;
const ROW_COLLAPSE_SCROLL_DELAY = 360;

const createEmptyField = (row) => ({
    row,
    name: generateFormId(12),
    label: "",
    labelAlt: "",
    required: "false",
    type: "",
});

const createInitialForm = (idModulo) => ({
    id: Number(idModulo || 0),
    label: "",
    createdAt: null,
    config: [],
});

const normalizeForm = (form, idModulo) => ({
    ...createInitialForm(idModulo),
    ...form,
    config: Array.isArray(form?.config) ? form.config : [],
});

const getRequiredValue = (value) => {
    if (value === true) return "true";
    if (value === false || value === undefined || value === null || value === "") return "false";
    return String(value);
};

const formatDate = (value) => {
    const date = moment(value);
    return date.isValid() ? date.format("DD/MM/YYYY HH:mm") : "Non disponibile";
};

const getFieldDisplayName = (field) => {
    const label = String(field?.label || "").trim();
    const helperText = String(field?.labelAlt || "").trim();
    const name = String(field?.name || "").trim();

    return label || helperText || name || "Campo senza nome";
};

const createOpenRowsState = (rows, activeRow = null, forceOpen = false) => (
    rows.reduce((nextRows, row) => ({
        ...nextRows,
        [row]: forceOpen || row === activeRow,
    }), {})
);

const GestioneModuliEdit = () => {
    const classes = CompanyStyle();
    const moduliClasses = GestioneModuliStyle();
    const history = useHistory();
    const location = useLocation();
    const {idModulo} = useParams();
    const rowRefs = useRef({});
    const receivedForm = location.state || {};

    const [dynamicForm, setDynamicForm] = useState(() => normalizeForm(receivedForm, idModulo));
    const [loading, setLoading] = useState(!Array.isArray(receivedForm.config) && idModulo !== "0");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [openSearchDialog, setOpenSearchDialog] = useState(false);
    const [validationOn, setValidationOn] = useState(false);
    const [openDialog2, setOpenDialog2] = useState(false);
    const [openRows, setOpenRows] = useState({});
    const [forceAllRowsOpen, setForceAllRowsOpen] = useState(false);
    const [rowJump, setRowJump] = useState(null);
    const [optionDrafts, setOptionDrafts] = useState({});

    useEffect(() => {
        if (Array.isArray(receivedForm.config) || idModulo === "0") return;

        setLoading(true);
        getDynamicFormConfig(idModulo)
            .then(data => {
                setDynamicForm(normalizeForm(data, idModulo));
                setError("");
            })
            .catch(loadError => {
                setError(loadError.message || "Non e stato possibile recuperare il modulo.");
            })
            .finally(() => setLoading(false));
    }, [idModulo, receivedForm.config]);

    useEffect(() => {
        if (loading) return;
        if ((dynamicForm.config || []).length > 0) return;

        setDynamicForm(prevForm => ({
            ...prevForm,
            config: [createEmptyField(0)],
        }));
    }, [dynamicForm.config, loading]);

    const groupedRows = useMemo(() => {
        const rows = new Map();

        (dynamicForm.config || []).forEach((field, originalIndex) => {
            const row = Number(field.row || 0);
            if (!rows.has(row)) rows.set(row, []);
            rows.get(row).push({field, originalIndex});
        });

        return [...rows.entries()]
            .sort(([firstRow], [secondRow]) => firstRow - secondRow)
            .map(([row, items]) => ({
                row,
                fields: items.map(({field}) => field),
            }));
    }, [dynamicForm.config]);

    const rowNumbers = useMemo(() => groupedRows.map(({row}) => row), [groupedRows]);
    const rowOptions = useMemo(() => groupedRows.map(({row, fields}) => {
        const fieldNames = fields.map(getFieldDisplayName).filter(Boolean);
        const fieldsPreview = fieldNames.length > 0 ? fieldNames.join(", ") : "Nessun campo nominato";
        const technicalSearchText = fields
            .flatMap(field => [field?.label, field?.labelAlt, field?.name, field?.type])
            .filter(Boolean)
            .join(" ");

        return {
            value: row,
            title: `Riga ${row + 1}`,
            label: `Riga ${row + 1} - ${fieldsPreview}`,
            description: `${fields.length} campi`,
            fieldsPreview,
            searchText: `riga ${row + 1} ${fields.length} campi ${fieldsPreview} ${technicalSearchText}`,
        };
    }), [groupedRows]);

    const renderRowOptionContent = useCallback((option) => (
        <div className={moduliClasses.rowJumpOption}>
            <div className={moduliClasses.rowJumpOptionHeader}>
                <Typography className={moduliClasses.rowJumpOptionTitle}>
                    {option.title}
                </Typography>
                <Typography className={moduliClasses.rowJumpOptionMeta}>
                    {option.description}
                </Typography>
            </div>
            <Typography className={moduliClasses.rowJumpOptionFields}>
                {option.fieldsPreview}
            </Typography>
        </div>
    ), [moduliClasses.rowJumpOption, moduliClasses.rowJumpOptionFields, moduliClasses.rowJumpOptionHeader, moduliClasses.rowJumpOptionMeta, moduliClasses.rowJumpOptionTitle]);

    const openRowSearchDialog = useCallback(() => {
        if (loading || error || rowOptions.length === 0) return;

        setOpenSearchDialog(true);
    }, [error, loading, rowOptions.length]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            const isFindShortcut = (event.ctrlKey || event.metaKey) && event.key?.toLowerCase() === "f";

            if (!isFindShortcut || loading || error || rowOptions.length === 0) return;

            event.preventDefault();
            event.stopPropagation();
            openRowSearchDialog();
        };

        window.addEventListener("keydown", handleKeyDown, true);

        return () => {
            window.removeEventListener("keydown", handleKeyDown, true);
        };
    }, [error, loading, openRowSearchDialog, rowOptions.length]);

    useEffect(() => {
        setOpenRows(prevRows => {
            const activeRow = rowNumbers.find(row => prevRows[row] === true) ?? rowNumbers[0] ?? null;

            return createOpenRowsState(rowNumbers, activeRow, forceAllRowsOpen);
        });
    }, [forceAllRowsOpen, rowNumbers]);

    const rowMap = useMemo(() => {
        const map = {};

        (dynamicForm.config || []).forEach((item, originalIndex) => {
            const row = Number(item.row || 0);
            if (!map[row]) map[row] = [];
            map[row].push({item, originalIndex});
        });

        return map;
    }, [dynamicForm.config]);

    const fieldCount = dynamicForm.config?.length || 0;
    const rowsCount = groupedRows.length;
    const createdAt = dynamicForm.createdAt || dynamicForm.dataCreazione || dynamicForm.creationDate;

    const handleMainChange = (key, value) => {
        setDynamicForm(prevForm => ({
            ...prevForm,
            [key]: value,
        }));
    };

    const handleChange = (row, index, key, value) => {
        setDynamicForm(prevForm => {
            const config = [...(prevForm.config || [])];
            const rowItems = config
                .map((field, originalIndex) => ({field, originalIndex}))
                .filter(({field}) => Number(field.row || 0) === Number(row));
            const target = rowItems[index];

            if (!target) return prevForm;

            config[target.originalIndex] = {
                ...config[target.originalIndex],
                [key]: value,
            };

            return {
                ...prevForm,
                config,
            };
        });
    };

    const canAddInRow = (row) => (
        (dynamicForm.config || []).filter(item => Number(item.row || 0) === Number(row)).length >= 4
    );

    const handleCreate = (row) => {
        if (canAddInRow(row)) return;
        setDynamicForm(prevForm => ({
            ...prevForm,
            config: [...(prevForm.config || []), createEmptyField(row)],
        }));
        setOpenRows(createOpenRowsState(rowNumbers, row, forceAllRowsOpen));
    };

    const handleDelete = (name, rowIndex) => {
        setDynamicForm(prevForm => {
            const config = (prevForm.config || []).filter(item => item.name !== name);
            const hasRowItems = config.some(item => Number(item.row || 0) === Number(rowIndex));
            const nextConfig = hasRowItems ? config : config.map(item => ({
                ...item,
                row: Number(item.row || 0) > Number(rowIndex) ? Number(item.row || 0) - 1 : Number(item.row || 0),
            }));

            return {
                ...prevForm,
                config: nextConfig,
            };
        });
    };

    const move = (name, row, direction) => {
        setDynamicForm(prevForm => {
            const rowItems = rowMap[row];
            if (!rowItems) return prevForm;

            const rowIndex = rowItems.findIndex(({item}) => item.name === name);
            const delta = direction === "left" ? -1 : direction === "right" ? 1 : 0;
            const targetIndex = rowIndex + delta;

            if (rowIndex === -1 || targetIndex < 0 || targetIndex >= rowItems.length) return prevForm;

            const config = [...(prevForm.config || [])];
            const current = rowItems[rowIndex].originalIndex;
            const target = rowItems[targetIndex].originalIndex;
            [config[current], config[target]] = [config[target], config[current]];

            return {
                ...prevForm,
                config,
            };
        });
    };

    const alignRowToViewport = (row, behavior = "smooth") => {
        const rowNode = rowRefs.current[row];
        if (!rowNode) return;

        window.requestAnimationFrame(() => {
            const targetTop = Math.max(
                rowNode.getBoundingClientRect().top + window.scrollY - ROW_SCROLL_OFFSET,
                0
            );

            window.scrollTo({
                top: targetTop,
                behavior,
            });
        });
    };

    const scrollToRow = (row, delay = 90, settleAfterCollapse = false) => {
        setRowJump(row);
        window.setTimeout(() => {
            alignRowToViewport(row);
        }, delay);

        if (settleAfterCollapse) {
            window.setTimeout(() => {
                alignRowToViewport(row, "auto");
            }, delay + ROW_COLLAPSE_SCROLL_DELAY);
        }
    };

    const setInsertedRowOpen = (insertRow) => {
        setOpenRows(prevRows => {
            if (!forceAllRowsOpen) {
                return {
                    [insertRow]: true,
                };
            }

            const nextRows = {};

            Object.entries(prevRows).forEach(([rowKey, isOpen]) => {
                const rowNumber = Number(rowKey);
                nextRows[rowNumber >= insertRow ? rowNumber + 1 : rowNumber] = isOpen;
            });

            return {
                ...nextRows,
                [insertRow]: true,
            };
        });
        scrollToRow(insertRow);
    };

    const handleCreateRow = (rowIndex, position) => {
        const insertRow = position === "UP" ? Number(rowIndex) : Number(rowIndex) + 1;

        setDynamicForm(prevForm => {
            const config = [...(prevForm.config || [])];
            const shiftedConfig = config.map(item => {
                const itemRow = Number(item.row || 0);
                const shouldShift = position === "UP" ? itemRow >= Number(rowIndex) : itemRow > Number(rowIndex);

                return shouldShift ? {...item, row: itemRow + 1} : {...item, row: itemRow};
            });

            return {
                ...prevForm,
                config: [...shiftedConfig, createEmptyField(insertRow)],
            };
        });
        setInsertedRowOpen(insertRow);
    };

    const handleDuplicateRow = (rowIndex, position = "DOWN") => {
        const insertRow = position === "UP" ? Number(rowIndex) : Number(rowIndex) + 1;

        setDynamicForm(prevForm => {
            const config = [...(prevForm.config || [])];
            const rowElements = config.filter(item => Number(item.row || 0) === Number(rowIndex));
            if (rowElements.length === 0) return prevForm;

            const shiftedConfig = config.map(item => {
                const itemRow = Number(item.row || 0);
                const shouldShift = position === "UP" ? itemRow >= Number(rowIndex) : itemRow > Number(rowIndex);

                return shouldShift ? {...item, row: itemRow + 1} : {...item, row: itemRow};
            });
            const duplicatedElements = rowElements.map(item => ({
                ...item,
                row: insertRow,
                name: generateFormId(12),
            }));

            return {
                ...prevForm,
                config: [...shiftedConfig, ...duplicatedElements],
            };
        });
        setInsertedRowOpen(insertRow);
    };

    const handleMoveRow = (row, direction) => {
        const currentRow = Number(row);
        const currentIndex = rowNumbers.indexOf(currentRow);
        const targetRow = rowNumbers[currentIndex + (direction === "UP" ? -1 : 1)];

        if (currentIndex === -1 || targetRow === undefined) return;

        setDynamicForm(prevForm => ({
            ...prevForm,
            config: (prevForm.config || []).map(item => {
                const itemRow = Number(item.row || 0);

                if (itemRow === currentRow) return {...item, row: targetRow};
                if (itemRow === targetRow) return {...item, row: currentRow};
                return item;
            }),
        }));
        setOpenRows(createOpenRowsState(rowNumbers, targetRow, forceAllRowsOpen));
        scrollToRow(targetRow);
    };

    const handleDeleteRow = (row) => {
        setDynamicForm(prevForm => {
            const nextConfig = (prevForm.config || [])
                .filter(item => Number(item.row || 0) !== Number(row))
                .map(item => {
                    const itemRow = Number(item.row || 0);
                    return {
                        ...item,
                        row: itemRow > Number(row) ? itemRow - 1 : itemRow,
                    };
                });

            return {
                ...prevForm,
                config: nextConfig,
            };
        });
        setOpenRows(() => {
            const deletedRow = Number(row);
            const remainingRows = rowNumbers
                .filter(rowNumber => rowNumber !== deletedRow)
                .map(rowNumber => rowNumber > deletedRow ? rowNumber - 1 : rowNumber);
            const nextActiveRow = remainingRows.find(rowNumber => rowNumber >= deletedRow)
                ?? remainingRows[remainingRows.length - 1]
                ?? null;

            return createOpenRowsState(remainingRows, nextActiveRow, forceAllRowsOpen);
        });
        setRowJump(null);
    };

    const handleSubmitChange = useCallback(() => {
        setSaving(true);
        updateOrCreateDynamicForm(dynamicForm)
            .then(data => {
                setDynamicForm(normalizeForm(data, idModulo));
                setOpenDialog2(true);
            })
            .catch(saveError => window.alert(saveError.message))
            .finally(() => setSaving(false));
    }, [dynamicForm, idModulo]);

    const handleCloseSaveDialog = () => {
        setOpenDialog2(false);
        history.goBack();
    };

    const handleOpenDialog = (initialValidation = false) => {
        setValidationOn(initialValidation);
        setOpenDialog(true);
    };

    const handleForceAllRowsOpenChange = (event) => {
        const enabled = event.target.checked;
        const activeRow = rowJump ?? rowNumbers.find(row => openRows[row] === true) ?? rowNumbers[0] ?? null;

        setForceAllRowsOpen(enabled);
        setOpenRows(createOpenRowsState(rowNumbers, activeRow, enabled));
    };

    const toggleRow = (row) => {
        if (forceAllRowsOpen) return;

        setOpenRows(prevRows => (
            prevRows[row] === true
                ? createOpenRowsState(rowNumbers)
                : createOpenRowsState(rowNumbers, row)
        ));
    };

    const handleJumpToRow = (row) => {
        if (row === null || row === undefined) return;
        setOpenRows(createOpenRowsState(rowNumbers, row, forceAllRowsOpen));
        scrollToRow(row, forceAllRowsOpen ? 120 : ROW_COLLAPSE_SCROLL_DELAY, !forceAllRowsOpen);
    };

    const handleQuickJumpToRow = (row) => {
        if (row === null || row === undefined) return;

        setOpenSearchDialog(false);
        handleJumpToRow(row);
    };

    const handleOptionDraftChange = (fieldName, value) => {
        setOptionDrafts(prevDrafts => ({
            ...prevDrafts,
            [fieldName]: value,
        }));
    };

    const handleAddOption = (row, index, field) => {
        const nextOption = (optionDrafts[field.name] || "").trim();
        if (!nextOption) return;

        handleChange(row, index, "options", [
            ...(field.options || []),
            {value: nextOption, label: nextOption},
        ]);
        handleOptionDraftChange(field.name, "");
    };

    return (
        <React.Fragment>
            <AppPage
                title={dynamicForm.label ? `Modifica modulo ${dynamicForm.label}` : "Modifica modulo"}
                appBarProps={{back: true, backOk: true}}
                mainClassName={classes.page}
                containerClassName={classes.container}
            >
                <Tooltip title="Cerca riga (Ctrl+F)">
                    <IconButton
                        aria-label="Cerca riga"
                        className={`${moduliClasses.previewFab} ${moduliClasses.searchFab}`}
                        disabled={loading || Boolean(error)}
                        onClick={openRowSearchDialog}
                        size="large"
                    >
                        <SearchTwoToneIcon/>
                    </IconButton>
                </Tooltip>

                <Tooltip title="Anteprima modulo">
                    <IconButton
                        aria-label="Anteprima modulo"
                        className={moduliClasses.previewFab}
                        onClick={() => handleOpenDialog(false)}
                        size="large"
                    >
                        <VisibilityTwoToneIcon/>
                    </IconButton>
                </Tooltip>

                <section className={`${classes.panel} ${moduliClasses.moduleEditPanel}`}>
                    <header className={classes.panelHeader}>
                        <div>
                            <Typography className={classes.panelTitle}>Configurazione modulo</Typography>
                            <Typography className={classes.panelSubtitle}>
                                Modifica nome, righe e campi del form dinamico.
                            </Typography>
                        </div>
                        <div className={moduliClasses.editHeaderActions}>
                            <span className={moduliClasses.moduleIdBadge}>
                                #{dynamicForm.id || "nuovo"}
                            </span>
                            <Button
                                className={classes.actionButton}
                                disabled={saving || loading}
                                disableElevation
                                onClick={handleSubmitChange}
                                size="small"
                                startIcon={saving ? <CircularProgress size={16}/> : <SaveTwoToneIcon/>}
                                variant="contained"
                            >
                                Salva modifiche
                            </Button>
                        </div>
                    </header>

                    {loading && (
                        <div className={moduliClasses.editLoading}>
                            <Skeleton className={classes.skeletonCard} variant="rounded"/>
                            <Skeleton className={classes.skeletonCard} variant="rounded"/>
                        </div>
                    )}

                    {!loading && error && (
                        <Alert severity="error" sx={{borderRadius: 4}}>
                            {error}
                        </Alert>
                    )}

                    {!loading && !error && (
                        <div className={moduliClasses.moduleEditTop}>
                            <SolidTextField
                                label="Nome modulo"
                                onChange={(event) => handleMainChange("label", event.target.value)}
                                size="small"
                                value={dynamicForm.label || ""}
                            />
                            <div className={moduliClasses.moduleEditFacts}>
                                <div className={moduliClasses.editFact}>
                                    <span>Righe</span>
                                    <strong>{rowsCount}</strong>
                                </div>
                                <div className={moduliClasses.editFact}>
                                    <span>Campi</span>
                                    <strong>{fieldCount}</strong>
                                </div>
                                <div className={moduliClasses.editFact}>
                                    <span>Creazione</span>
                                    <strong>{formatDate(createdAt)}</strong>
                                </div>
                            </div>
                        </div>
                    )}
                </section>

                {!loading && !error && (
                    <section className={`${classes.panel} ${moduliClasses.moduleEditPanel}`}>
                        <header className={classes.panelHeader}>
                            <div>
                                <Typography className={classes.panelTitle}>Righe del modulo</Typography>
                                <Typography className={classes.panelSubtitle}>
                                    Ogni riga puo contenere fino a quattro campi e puo essere aperta o chiusa.
                                </Typography>
                            </div>
                            <div className={moduliClasses.rowsToolbar}>
                                <AutocompleteSelect
                                    className={moduliClasses.rowJumpSelect}
                                    label="Vai a riga"
                                    onChange={handleJumpToRow}
                                    options={rowOptions}
                                    renderOptionContent={renderRowOptionContent}
                                    size="small"
                                    value={rowJump}
                                />
                                <label className={moduliClasses.rowsModeToggle}>
                                    <span className={moduliClasses.rowsModeLabel}>Tutte aperte</span>
                                    <Switch
                                        checked={forceAllRowsOpen}
                                        inputProps={{"aria-label": "Forza tutte le righe aperte"}}
                                        onChange={handleForceAllRowsOpenChange}
                                        size="small"
                                    />
                                </label>
                                <Button
                                    className={classes.actionButton}
                                    disableElevation
                                    onClick={() => handleCreateRow(rowNumbers[rowNumbers.length - 1] || 0, "DOWN")}
                                    size="small"
                                    startIcon={<AddTwoToneIcon/>}
                                    variant="contained"
                                >
                                    Nuova riga
                                </Button>
                            </div>
                        </header>

                        <div className={moduliClasses.editRowsStack}>
                            {groupedRows.map(({row, fields}) => {
                                const isRowOpen = forceAllRowsOpen || openRows[row] === true;
                                const rowPosition = rowNumbers.indexOf(row);
                                const isFirstRow = rowPosition === 0;
                                const isLastRow = rowPosition === rowNumbers.length - 1;

                                return (
                                    <section
                                        className={moduliClasses.editRowCard}
                                        key={row}
                                        ref={(node) => {
                                            rowRefs.current[row] = node;
                                        }}
                                    >
                                        <header className={moduliClasses.editRowHeader}>
                                            <button
                                                className={moduliClasses.editRowToggle}
                                                onClick={() => toggleRow(row)}
                                                type="button"
                                            >
                                                <span className={moduliClasses.editRowIcon}>
                                                    {isRowOpen ? <ExpandLessTwoToneIcon/> : <ExpandMoreTwoToneIcon/>}
                                                </span>
                                                <span>
                                                    <Typography component="span" className={moduliClasses.editRowTitle}>
                                                        Riga {row + 1}
                                                    </Typography>
                                                    <Typography component="span" className={moduliClasses.editRowMeta}>
                                                        {fields.length} campi
                                                    </Typography>
                                                </span>
                                            </button>

                                            <div className={moduliClasses.editRowActions}>
                                                <div className={moduliClasses.rowActionGroup}>
                                                    <span className={moduliClasses.rowActionGroupLabel}>Campo</span>
                                                    <Tooltip title={canAddInRow(row) ? "Limite di quattro campi raggiunto" : "Aggiungi campo"}>
                                                        <span>
                                                            <Button
                                                                aria-label="Aggiungi campo"
                                                                className={moduliClasses.rowActionButton}
                                                                disabled={canAddInRow(row)}
                                                                onClick={() => handleCreate(row)}
                                                                size="small"
                                                                startIcon={<AddTwoToneIcon/>}
                                                            >
                                                                Aggiungi
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                </div>

                                                <div className={moduliClasses.rowActionGroup}>
                                                    <span className={moduliClasses.rowActionGroupLabel}>Inserisci</span>
                                                    <Tooltip title="Aggiungi riga sopra">
                                                        <Button aria-label="Aggiungi riga sopra" className={moduliClasses.rowActionButton} onClick={() => handleCreateRow(row, "UP")} size="small" startIcon={<ArrowUpwardRoundedIcon/>}>
                                                            Sopra
                                                        </Button>
                                                    </Tooltip>
                                                    <Tooltip title="Aggiungi riga sotto">
                                                        <Button aria-label="Aggiungi riga sotto" className={moduliClasses.rowActionButton} onClick={() => handleCreateRow(row, "DOWN")} size="small" startIcon={<ArrowDownwardRoundedIcon/>}>
                                                            Sotto
                                                        </Button>
                                                    </Tooltip>
                                                </div>

                                                <div className={moduliClasses.rowActionGroup}>
                                                    <span className={moduliClasses.rowActionGroupLabel}>Sposta</span>
                                                    <Tooltip title={isFirstRow ? "La riga e gia in alto" : "Sposta riga sopra"}>
                                                        <span>
                                                            <Button
                                                                aria-label="Sposta riga sopra"
                                                                className={moduliClasses.rowActionButton}
                                                                disabled={isFirstRow}
                                                                onClick={() => handleMoveRow(row, "UP")}
                                                                size="small"
                                                                startIcon={<ArrowUpwardRoundedIcon/>}
                                                            >
                                                                Sopra
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title={isLastRow ? "La riga e gia in basso" : "Sposta riga sotto"}>
                                                        <span>
                                                            <Button
                                                                aria-label="Sposta riga sotto"
                                                                className={moduliClasses.rowActionButton}
                                                                disabled={isLastRow}
                                                                onClick={() => handleMoveRow(row, "DOWN")}
                                                                size="small"
                                                                startIcon={<ArrowDownwardRoundedIcon/>}
                                                            >
                                                                Sotto
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                </div>

                                                <div className={moduliClasses.rowActionGroup}>
                                                    <span className={moduliClasses.rowActionGroupLabel}>Duplica</span>
                                                    <Tooltip title="Duplica riga sopra">
                                                        <Button aria-label="Duplica riga sopra" className={moduliClasses.rowActionButton} onClick={() => handleDuplicateRow(row, "UP")} size="small" startIcon={<ContentCopyTwoToneIcon/>}>
                                                            Sopra
                                                        </Button>
                                                    </Tooltip>
                                                    <Tooltip title="Duplica riga sotto">
                                                        <Button aria-label="Duplica riga sotto" className={moduliClasses.rowActionButton} onClick={() => handleDuplicateRow(row, "DOWN")} size="small" startIcon={<ContentCopyTwoToneIcon/>}>
                                                            Sotto
                                                        </Button>
                                                    </Tooltip>
                                                </div>
                                            </div>

                                            <div className={moduliClasses.rowDeleteAction}>
                                                <Tooltip title="Elimina riga">
                                                    <IconButton aria-label="Elimina riga" className={moduliClasses.editIconButtonDanger} onClick={() => handleDeleteRow(row)} size="small">
                                                        <DeleteTwoToneIcon/>
                                                    </IconButton>
                                                </Tooltip>
                                            </div>
                                        </header>

                                        <Collapse in={isRowOpen} timeout="auto" unmountOnExit>
                                            <div className={moduliClasses.editFieldsGrid}>
                                                {fields.map((field, index) => (
                                                    <article className={moduliClasses.editFieldCard} key={`${field.name}-${index}`}>
                                                        <header className={moduliClasses.editFieldHeader}>
                                                            <div>
                                                                <Typography className={moduliClasses.editFieldTitle}>
                                                                    Campo {index + 1}
                                                                </Typography>
                                                                <Typography className={moduliClasses.editFieldMeta}>
                                                                    {field.name || "Nome JSON non disponibile"}
                                                                </Typography>
                                                            </div>
                                                            <div className={moduliClasses.editFieldActions}>
                                                                <IconButton
                                                                    aria-label="Sposta campo a sinistra"
                                                                    className={moduliClasses.editIconButton}
                                                                    disabled={index === 0}
                                                                    onClick={() => move(field.name, field.row, "left")}
                                                                    size="small"
                                                                >
                                                                    <ArrowBackTwoToneIcon/>
                                                                </IconButton>
                                                                <IconButton
                                                                    aria-label="Elimina campo"
                                                                    className={moduliClasses.editIconButtonDanger}
                                                                    onClick={() => handleDelete(field.name, field.row)}
                                                                    size="small"
                                                                >
                                                                    <DeleteTwoToneIcon/>
                                                                </IconButton>
                                                                <IconButton
                                                                    aria-label="Sposta campo a destra"
                                                                    className={moduliClasses.editIconButton}
                                                                    disabled={index >= fields.length - 1}
                                                                    onClick={() => move(field.name, field.row, "right")}
                                                                    size="small"
                                                                >
                                                                    <ArrowForwardTwoToneIcon/>
                                                                </IconButton>
                                                            </div>
                                                        </header>

                                                        <div className={moduliClasses.editFieldGrid}>
                                                            <SolidTextField
                                                                disabled
                                                                label="Nome JSON"
                                                                onChange={(event) => handleChange(row, index, "name", event.target.value)}
                                                                size="small"
                                                                value={field.name || ""}
                                                            />
                                                            <SolidTextField
                                                                label="Nome visualizzato"
                                                                onChange={(event) => handleChange(row, index, "label", event.target.value)}
                                                                size="small"
                                                                value={field.label || ""}
                                                            />
                                                            <SolidTextField
                                                                label="HelperText"
                                                                onChange={(event) => handleChange(row, index, "labelAlt", event.target.value)}
                                                                size="small"
                                                                value={field.labelAlt || ""}
                                                            />
                                                            <SolidTextField
                                                                label="Dependency"
                                                                onChange={(event) => handleChange(row, index, "dependency", event.target.value)}
                                                                size="small"
                                                                value={field.dependency || ""}
                                                            />
                                                            <SolidTextField
                                                                label="DependencyValue"
                                                                onChange={(event) => handleChange(row, index, "dependencyValue", event.target.value)}
                                                                size="small"
                                                                value={field.dependencyValue || ""}
                                                            />
                                                            <AutocompleteSelect
                                                                disableClearable
                                                                label="Obbligatorieta"
                                                                onChange={(value) => handleChange(row, index, "required", value)}
                                                                options={requiredOptions}
                                                                size="small"
                                                                value={getRequiredValue(field.required)}
                                                            />
                                                            <AutocompleteSelect
                                                                label="Tipo"
                                                                onChange={(value) => handleChange(row, index, "type", value || "")}
                                                                options={fieldTypeOptions}
                                                                renderOptionContent={(option) => (
                                                                    <div>
                                                                        <Typography variant="body2" style={{fontWeight: 850}}>
                                                                            {option.label}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="textSecondary">
                                                                            {option.group}
                                                                        </Typography>
                                                                    </div>
                                                                )}
                                                                size="small"
                                                                value={field.type || null}
                                                            />
                                                        </div>

                                                        {fieldsWithOptions.includes(field.type) && (
                                                            <section className={moduliClasses.optionsEditor}>
                                                                <div className={moduliClasses.optionInputRow}>
                                                                    <SolidTextField
                                                                        label="Aggiungi opzione"
                                                                        onChange={(event) => handleOptionDraftChange(field.name, event.target.value)}
                                                                        handleKeyDown={(event) => {
                                                                            if (event.key === "Enter") {
                                                                                event.preventDefault();
                                                                                handleAddOption(row, index, field);
                                                                            }
                                                                        }}
                                                                        size="small"
                                                                        value={optionDrafts[field.name] || ""}
                                                                    />
                                                                    <Button
                                                                        className={classes.actionButton}
                                                                        disableElevation
                                                                        onClick={() => handleAddOption(row, index, field)}
                                                                        size="small"
                                                                        startIcon={<AddTwoToneIcon/>}
                                                                        variant="outlined"
                                                                    >
                                                                        Aggiungi
                                                                    </Button>
                                                                </div>
                                                                <div className={moduliClasses.optionChips}>
                                                                    {(field.options || []).map((option) => (
                                                                        <Chip
                                                                            className={moduliClasses.optionChip}
                                                                            key={option.value || option.label}
                                                                            label={option.label || option.value}
                                                                            onDelete={() => {
                                                                                const updatedOptions = (field.options || []).filter(opt => opt.value !== option.value);
                                                                                handleChange(row, index, "options", updatedOptions);
                                                                            }}
                                                                            size="small"
                                                                        />
                                                                    ))}
                                                                </div>
                                                            </section>
                                                        )}
                                                    </article>
                                                ))}
                                            </div>
                                        </Collapse>
                                    </section>
                                );
                            })}
                        </div>
                    </section>
                )}
            </AppPage>

            <SimpleDialog
                open={openSearchDialog}
                handleClose={() => setOpenSearchDialog(false)}
                title="Vai a riga"
                subtitle="Cerca per numero riga o nome dei campi."
                maxWidth="sm"
                showCloseButton
                closeOnBackdropClick
                content={(
                    <div className={moduliClasses.quickSearchDialog}>
                        <AutocompleteSelect
                            autoFocus
                            className={moduliClasses.quickSearchSelect}
                            label="Cerca riga"
                            onChange={handleQuickJumpToRow}
                            options={rowOptions}
                            placeholder="Scrivi riga, campo, label o tipo"
                            renderOptionContent={renderRowOptionContent}
                            size="small"
                            value={null}
                        />
                    </div>
                )}
            />

            <SimpleDialog
                open={openDialog}
                handleClose={() => setOpenDialog(false)}
                title="Anteprima modulo"
                subtitle="Controlla layout e campi come verranno esposti all'esterno."
                maxWidth="md"
                showCloseButton
                closeOnBackdropClick
                content={(
                    <DynamicFormExt
                        clienteCensito={false}
                        fields={dynamicForm.config}
                        onDataChange={() => {}}
                        initialValidation={validationOn}
                    />
                )}
                actions={(
                    <DialogActions className={classes.dialogActions}>
                        <Button
                            className={classes.actionButton}
                            disableElevation
                            onClick={() => handleOpenDialog(true)}
                            size="small"
                            variant="outlined"
                        >
                            Anteprima con validazione
                        </Button>
                    </DialogActions>
                )}
            />

            <SimpleDialog
                open={openDialog2}
                handleClose={handleCloseSaveDialog}
                title="Esito salvataggio"
                maxWidth="sm"
                showCloseButton
                closeOnBackdropClick
                content={(
                    <Typography className={classes.panelSubtitle}>
                        Dati salvati correttamente.
                    </Typography>
                )}
                actions={(
                    <DialogActions className={classes.dialogActions}>
                        <Button className={classes.actionButton} disableElevation onClick={handleCloseSaveDialog} size="small" variant="contained">
                            Torna ai moduli
                        </Button>
                    </DialogActions>
                )}
            />
        </React.Fragment>
    );
};

export default React.memo(GestioneModuliEdit);
